import { CheckCircleIcon, ChevronDownIcon, TagIcon, XMarkIcon } from "@heroicons/react/20/solid";
import { useEffect, useMemo, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { Link, useSearchParams } from "react-router-dom";

import BusinessTypePicker from "../components/BusinessTypePicker";
import PhoneInput from "../components/PhoneInput";
import PublicHeader from "../components/PublicHeader";
import { apiErrorMessage, apiFieldErrors } from "../services/api";
import { fetchCountries } from "../services/lookupService";
import {
  fetchPublicBusinessTypes,
  fetchPublicPlans,
  pickCycle,
  planText,
  signupBusiness,
  validatePromoCode,
} from "../services/signupService";
import { Money, PlanPrice, PUBLIC_BACKDROP } from "./Pricing";
import { labelFor } from "../services/enumLabels";

const GENDERS = ["both", "female", "male"];

/** The Terms and Privacy content pages live on the customer portal. */
const CUSTOMER_PORTAL_URL = (process.env.REACT_APP_CUSTOMER_PORTAL_URL || "").replace(/\/+$/, "");

/** A content page link in a new tab, or plain text when the portal URL is not set. */
function ContentLink({ slug, children }) {
  if (!CUSTOMER_PORTAL_URL) return <>{children}</>;
  return (
    <a
      href={`${CUSTOMER_PORTAL_URL}/${slug}`}
      target="_blank"
      rel="noopener noreferrer"
      className="font-semibold text-accent-text underline-offset-2 hover:underline"
    >
      {children}
    </a>
  );
}

/** Fields that live on step 1 — a 422 on any of them sends the user back there. */
const STEP_ONE = ["name", "country_id", "served_gender", "business_types", "plan_id"];

const EMPTY = {
  name: "",
  country_id: "",
  served_gender: "",
  business_types: [],
  owner_name: "",
  owner_email: "",
  owner_phone: "",
  owner_phone_country: "",
  owner_password: "",
  owner_password_confirmation: "",
  accept_terms: false,
  website: "", // honeypot
};

/** One line of the summary: label on one side, amount on the other. */
function SummaryLine({ label, children, strong = false }) {
  return (
    <div
      className={`flex items-baseline justify-between gap-3 ${strong ? "font-bold text-ink" : "text-ink-2"}`}
    >
      <dt className="min-w-0">{label}</dt>
      <dd className="shrink-0">{children}</dd>
    </div>
  );
}

/**
 * The chosen plan and cycle, the server's line items (subtotal, cycle
 * discount, promo discount, total) and the promo code box. Every amount is
 * displayed as the API computed it.
 */
function OrderSummary({
  plan,
  cycles,
  cycleId,
  onCycle,
  quote,
  promo,
  onApplyPromo,
  onRemovePromo,
  promoState,
}) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const [open, setOpen] = useState(false);
  const [code, setCode] = useState("");
  const currency = plan.currency;
  const offered = cycles.filter((c) => plan.prices?.[c.id]);

  const apply = async (e) => {
    e.preventDefault();
    if (!code.trim()) return;
    if (await onApplyPromo(code.trim())) setCode("");
  };

  return (
    <div className="mb-6 rounded-xl border border-accent-line bg-accent-wash px-4 py-3">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="text-xs font-semibold uppercase tracking-wide text-accent-text">
            {t("signup.yourPlan")}
          </div>
          <div className="flex flex-wrap items-baseline gap-x-3">
            <span className="text-card-title font-bold text-ink">{planText(plan.name, lang)}</span>
            <PlanPrice plan={plan} quote={quote} size="sm" />
          </div>
        </div>
        <Link to="/pricing" className="text-sm font-semibold text-accent-text hover:underline">
          {t("signup.changePlan")}
        </Link>
      </div>

      {offered.length > 1 && (
        <div className="mt-3">
          <label className="field-label" htmlFor="billing_cycle">
            {t("signup.cycle")}
          </label>
          <select
            id="billing_cycle"
            className="field-input"
            value={cycleId ?? ""}
            onChange={(e) => onCycle(e.target.value)}
            disabled={promoState.busy}
          >
            {offered.map((c) => (
              <option key={c.id} value={c.id}>
                {planText(c.name, lang)}
                {Number(plan.prices[c.id].cycle_discount_percent) > 0
                  ? ` — ${t("pricing.saveBadge", { percent: Number(plan.prices[c.id].cycle_discount_percent) })}`
                  : ""}
              </option>
            ))}
          </select>
        </div>
      )}

      {quote && !plan.is_free && (
        <dl className="mt-3 flex flex-col gap-1 border-t border-accent-line pt-3 text-sm">
          <SummaryLine
            label={t("signup.monthsTimes", {
              count: quote.months,
              price: `${quote.monthly_price}${currency ? ` ${currency.symbol || currency.code}` : ""}`,
            })}
          >
            <Money amount={quote.subtotal} currency={currency} />
          </SummaryLine>
          {Number(quote.cycle_discount_amount) > 0 && (
            <SummaryLine label={t("signup.cycleDiscount", { percent: Number(quote.cycle_discount_percent) })}>
              <span className="text-accent-text">
                −<Money amount={quote.cycle_discount_amount} currency={currency} />
              </span>
            </SummaryLine>
          )}
          {promo && (
            <SummaryLine
              label={
                <span className="inline-flex flex-wrap items-center gap-2">
                  {t("signup.promoDiscount")}
                  <span
                    dir="ltr"
                    className="inline-flex items-center gap-1 rounded-full border border-accent-line bg-surface px-2 py-0.5 font-mono text-xs font-semibold text-ink"
                  >
                    <TagIcon className="size-3.5 text-accent-text" aria-hidden="true" />
                    {promo.code}
                    <button
                      type="button"
                      onClick={onRemovePromo}
                      className="-me-1 rounded-full p-0.5 text-ink-2 hover:text-danger-text"
                      aria-label={t("signup.promoRemove", { code: promo.code })}
                    >
                      <XMarkIcon className="size-3.5" aria-hidden="true" />
                    </button>
                  </span>
                </span>
              }
            >
              <span className="text-accent-text">
                −<Money amount={quote.promo_discount_amount} currency={currency} />
              </span>
            </SummaryLine>
          )}
          <SummaryLine label={t("signup.total")} strong>
            <Money amount={quote.total} currency={currency} />
          </SummaryLine>
          {quote.months > 1 && (
            <p className="text-end text-caption text-ink-2">
              {t("signup.perMonthEquivalent", {
                amount: `${quote.monthly_equivalent}${currency ? ` ${currency.symbol || currency.code}` : ""}`,
              })}
            </p>
          )}
          <p className="text-caption text-ink-2">{t("signup.noPaymentToday")}</p>
        </dl>
      )}

      {promoState.notice && (
        <p className="mt-2 text-sm text-warning-text" role="status">
          {promoState.notice}
        </p>
      )}

      {!plan.is_free && !promo && (
        <div className="mt-3 border-t border-accent-line pt-3">
          <button
            type="button"
            className="inline-flex items-center gap-1 text-sm font-semibold text-accent-text hover:underline"
            aria-expanded={open}
            aria-controls="promo-box"
            onClick={() => setOpen((v) => !v)}
          >
            {t("signup.promoToggle")}
            <ChevronDownIcon
              className={`size-4 transition-transform motion-reduce:transition-none ${open ? "rotate-180" : ""}`}
              aria-hidden="true"
            />
          </button>
          {open && (
            <form id="promo-box" className="mt-2" onSubmit={apply} noValidate>
              <label className="sr-only" htmlFor="promo_code">
                {t("signup.promoLabel")}
              </label>
              <div className="flex gap-2">
                <input
                  id="promo_code"
                  dir="ltr"
                  className={`field-input flex-1 font-mono uppercase ${promoState.error ? "border-danger" : ""}`}
                  value={code}
                  onChange={(e) => setCode(e.target.value.toUpperCase())}
                  placeholder={t("signup.promoPlaceholder")}
                  maxLength={10}
                  autoComplete="off"
                  aria-invalid={!!promoState.error}
                  aria-describedby={promoState.error ? "promo_code_error" : undefined}
                />
                <button type="submit" className="btn-secondary" disabled={promoState.busy || !code.trim()}>
                  {promoState.busy ? t("signup.promoApplying") : t("signup.promoApply")}
                </button>
              </div>
              {promoState.error && (
                <p id="promo_code_error" className="field-error">
                  {promoState.error}
                </p>
              )}
            </form>
          )}
        </div>
      )}
    </div>
  );
}

function FieldError({ errors, name }) {
  const message =
    errors[name]?.[0] ?? Object.entries(errors).find(([key]) => key.startsWith(`${name}.`))?.[1]?.[0];
  return message ? <p className="field-error">{message}</p> : null;
}

export default function Register({ onLogin }) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const [params] = useSearchParams();
  const planId = params.get("plan");

  const [plans, setPlans] = useState(null);
  const [cycles, setCycles] = useState([]);
  const [defaultCycleId, setDefaultCycleId] = useState(null);
  const [cycleId, setCycleId] = useState(null);
  // The applied code and the server's quote with it; null when none.
  const [promo, setPromo] = useState(null);
  const [promoState, setPromoState] = useState({ busy: false, error: "", notice: "" });
  const [countries, setCountries] = useState([]);
  const [types, setTypes] = useState([]);
  const [loadError, setLoadError] = useState("");

  const [step, setStep] = useState(1);
  const [form, setForm] = useState(EMPTY);
  const [phoneTouched, setPhoneTouched] = useState(false);
  const [errors, setErrors] = useState({});
  const [formError, setFormError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    Promise.all([fetchPublicPlans(), fetchCountries(), fetchPublicBusinessTypes()])
      .then(([p, c, bt]) => {
        if (cancelled) return;
        setPlans(p.plans);
        setCycles(p.billingCycles);
        setDefaultCycleId(p.defaultCycleId);
        setCountries(c);
        setTypes(bt);
      })
      .catch((err) => !cancelled && setLoadError(apiErrorMessage(err, t("signup.loadFailed"))));
    return () => {
      cancelled = true;
    };
  }, [lang, t]);

  const plan = useMemo(() => (plans ?? []).find((p) => p.id === planId), [plans, planId]);

  // The cycle carried from the pricing page, when this plan offers it.
  useEffect(() => {
    if (plan) setCycleId((current) => pickCycle(plan, current ?? params.get("cycle"), defaultCycleId));
  }, [plan, params, defaultCycleId]);

  const quote = promo?.quote ?? plan?.prices?.[cycleId] ?? null;

  /** Ask the server whether a code works for this plan and cycle. */
  const checkPromo = (code, forCycle) => validatePromoCode({ code, planId, cycleId: forCycle });

  const promoErrorText = (err) =>
    apiFieldErrors(err).promo_code?.[0] ??
    apiFieldErrors(err).billing_cycle_id?.[0] ??
    apiErrorMessage(err, t("signup.promoFailed"));

  const applyPromo = async (code) => {
    setPromoState({ busy: true, error: "", notice: "" });
    try {
      const promoQuote = await checkPromo(code, cycleId);
      setPromo({ code: promoQuote.promo?.code ?? code, quote: promoQuote });
      setPromoState({ busy: false, error: "", notice: "" });
      return true;
    } catch (err) {
      setPromoState({ busy: false, error: promoErrorText(err), notice: "" });
      return false;
    }
  };

  const removePromo = () => {
    setPromo(null);
    setPromoState({ busy: false, error: "", notice: "" });
  };

  /** A new cycle re-checks the applied code; one that no longer applies is dropped, with the reason. */
  const changeCycle = async (id) => {
    setCycleId(id);
    if (!promo) return;
    setPromoState({ busy: true, error: "", notice: "" });
    try {
      const promoQuote = await checkPromo(promo.code, id);
      setPromo({ code: promo.code, quote: promoQuote });
      setPromoState({ busy: false, error: "", notice: "" });
    } catch (err) {
      setPromo(null);
      setPromoState({
        busy: false,
        error: "",
        notice: t("signup.promoRemoved", { code: promo.code, reason: promoErrorText(err) }),
      });
    }
  };

  const set = (name, value) => {
    setForm((f) => ({ ...f, [name]: value }));
    setErrors((e) => (e[name] ? { ...e, [name]: undefined } : e));
  };

  const chooseCountry = (id) => {
    set("country_id", id);
    // The owner's phone follows the business's country until they pick one.
    if (!phoneTouched) {
      const iso = countries.find((c) => String(c.id) === String(id))?.iso_code;
      if (iso) set("owner_phone_country", iso);
    }
  };

  /** Step 1's required fields, checked here so "Next" says what is missing. */
  const nextStep = (e) => {
    e.preventDefault();
    const missing = {};
    if (!form.name.trim()) missing.name = [t("signup.required")];
    if (!form.country_id) missing.country_id = [t("signup.required")];
    if (!form.served_gender) missing.served_gender = [t("signup.required")];
    if (form.business_types.length === 0) missing.business_types = [t("signup.required")];
    setErrors(missing);
    if (Object.keys(missing).length === 0) setStep(2);
  };

  const submit = async (e) => {
    e.preventDefault();
    setFormError("");
    setErrors({});

    if (form.owner_password !== form.owner_password_confirmation) {
      setErrors({ owner_password: [t("signup.passwordMismatch")] });
      return;
    }

    setSubmitting(true);
    try {
      const { user } = await signupBusiness({
        ...form,
        plan_id: planId,
        billing_cycle_id: cycleId,
        promo_code: promo?.code ?? null,
        country_id: Number(form.country_id),
        owner_phone_country: form.owner_phone_country || null,
        preferred_language: lang,
      });
      onLogin?.(user);
    } catch (err) {
      const fieldErrors = apiFieldErrors(err);
      setErrors(fieldErrors);
      // The code was re-checked on submit and no longer applies: drop it, say why.
      if (fieldErrors.promo_code && promo) {
        setPromo(null);
        setPromoState({
          busy: false,
          error: "",
          notice: t("signup.promoRemoved", { code: promo.code, reason: fieldErrors.promo_code[0] }),
        });
      }
      if (Object.keys(fieldErrors).some((key) => STEP_ONE.includes(key.split(".")[0]))) setStep(1);
      if (
        Object.keys(fieldErrors).length === 0 ||
        fieldErrors.plan_id ||
        fieldErrors.billing_cycle_id ||
        fieldErrors.website
      ) {
        setFormError(apiErrorMessage(err, t("signup.failed")));
      }
    } finally {
      setSubmitting(false);
    }
  };

  const invalid = (name) => (errors[name] ? "border-danger" : "");

  let body;
  if (loadError) {
    body = <p className="text-danger-text">{loadError}</p>;
  } else if (plans === null) {
    body = (
      <div className="motion-safe:animate-pulse" aria-busy="true">
        <div className="mb-4 h-16 rounded-xl bg-line" />
        <div className="mb-3 h-10 rounded bg-line" />
        <div className="h-10 rounded bg-line" />
      </div>
    );
  } else if (!plan) {
    body = (
      <div className="text-center">
        <p className="mb-4 text-ink-2">{t("signup.noPlan")}</p>
        <Link to="/pricing" className="btn-primary inline-block">
          {t("signup.seePlans")}
        </Link>
      </div>
    );
  } else {
    body = (
      <>
        {/* The chosen plan and cycle, the price line items, the promo code. */}
        <OrderSummary
          plan={plan}
          cycles={cycles}
          cycleId={cycleId}
          onCycle={changeCycle}
          quote={quote}
          promo={promo}
          onApplyPromo={applyPromo}
          onRemovePromo={removePromo}
          promoState={promoState}
        />

        {/* Steps */}
        <ol className="mb-6 flex items-center gap-3 text-sm">
          {[1, 2].map((n) => (
            <li key={n} className="flex flex-1 items-center gap-2">
              <span
                className={`flex size-7 shrink-0 items-center justify-center rounded-full text-xs font-bold ${
                  step > n
                    ? "bg-accent text-accent-on"
                    : step === n
                      ? "border-2 border-accent text-ink"
                      : "border border-line-strong text-ink-2"
                }`}
              >
                {step > n ? <CheckCircleIcon className="size-5" aria-hidden="true" /> : n}
              </span>
              <span className={step === n ? "font-semibold text-ink" : "text-ink-2"}>
                {t(`signup.step${n}`)}
              </span>
              {n === 1 && <span className="h-px flex-1 bg-line" aria-hidden="true" />}
            </li>
          ))}
        </ol>

        {formError && (
          <div
            className="mb-4 rounded-lg border border-danger-line bg-danger-wash px-3 py-2 text-sm text-danger-text"
            role="alert"
          >
            {formError}
          </div>
        )}

        {step === 1 ? (
          <form onSubmit={nextStep} noValidate>
            <label className="field-label" htmlFor="name">
              {t("signup.businessName")} <span className="text-danger-text">*</span>
            </label>
            <input
              id="name"
              className={`field-input ${invalid("name")}`}
              value={form.name}
              onChange={(e) => set("name", e.target.value)}
              maxLength={255}
            />
            <FieldError errors={errors} name="name" />

            <label className="field-label mt-4" htmlFor="country_id">
              {t("signup.country")} <span className="text-danger-text">*</span>
            </label>
            <select
              id="country_id"
              className={`field-input ${invalid("country_id")}`}
              value={form.country_id}
              onChange={(e) => chooseCountry(e.target.value)}
            >
              <option value="">{t("signup.chooseCountry")}</option>
              {countries.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.flag ? `${c.flag} ` : ""}
                  {c.name}
                </option>
              ))}
            </select>
            <FieldError errors={errors} name="country_id" />

            <fieldset className="mt-4">
              <legend className="field-label">
                {t("signup.servedGender")} <span className="text-danger-text">*</span>
              </legend>
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
                {GENDERS.map((g) => (
                  <label
                    key={g}
                    className={`flex min-h-touch cursor-pointer items-center gap-2 rounded-input border px-3 text-dense ${
                      form.served_gender === g
                        ? "border-accent bg-accent-wash font-semibold"
                        : "border-line-strong"
                    }`}
                  >
                    <input
                      type="radio"
                      name="served_gender"
                      value={g}
                      checked={form.served_gender === g}
                      onChange={() => set("served_gender", g)}
                      className="accent-[var(--pl-accent)]"
                    />
                    {labelFor("gender", g)}
                  </label>
                ))}
              </div>
              <FieldError errors={errors} name="served_gender" />
            </fieldset>

            <div className="mt-4">
              <BusinessTypePicker
                businessTypes={types}
                selected={form.business_types}
                onChange={(ids) => set("business_types", ids)}
                error={errors.business_types?.[0] ?? errors["business_types.0"]?.[0]}
              />
            </div>

            <button type="submit" className="btn-primary mt-6 w-full">
              {t("signup.next")}
            </button>
          </form>
        ) : (
          <form onSubmit={submit} noValidate>
            <label className="field-label" htmlFor="owner_name">
              {t("signup.ownerName")} <span className="text-danger-text">*</span>
            </label>
            <input
              id="owner_name"
              className={`field-input ${invalid("owner_name")}`}
              value={form.owner_name}
              onChange={(e) => set("owner_name", e.target.value)}
              autoComplete="name"
              required
            />
            <FieldError errors={errors} name="owner_name" />

            <label className="field-label mt-4" htmlFor="owner_email">
              {t("signup.ownerEmail")} <span className="text-danger-text">*</span>
            </label>
            <input
              id="owner_email"
              type="email"
              dir="ltr"
              className={`field-input ${invalid("owner_email")}`}
              value={form.owner_email}
              onChange={(e) => set("owner_email", e.target.value)}
              autoComplete="email"
              required
            />
            <FieldError errors={errors} name="owner_email" />

            <label className="field-label mt-4" htmlFor="owner_phone">
              {t("signup.ownerPhone")} <span className="text-danger-text">*</span>
            </label>
            <PhoneInput
              id="owner_phone"
              countries={countries}
              country={form.owner_phone_country}
              onCountryChange={(iso) => {
                setPhoneTouched(true);
                set("owner_phone_country", iso);
              }}
              value={form.owner_phone}
              onChange={(e) => set("owner_phone", e.target.value)}
              placeholder={t("phone.placeholder")}
              invalid={!!(errors.owner_phone || errors.owner_phone_country)}
            />
            <FieldError errors={errors} name="owner_phone" />
            <FieldError errors={errors} name="owner_phone_country" />

            <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <label className="field-label" htmlFor="owner_password">
                  {t("signup.password")} <span className="text-danger-text">*</span>
                </label>
                <input
                  id="owner_password"
                  type="password"
                  className={`field-input ${invalid("owner_password")}`}
                  value={form.owner_password}
                  onChange={(e) => set("owner_password", e.target.value)}
                  autoComplete="new-password"
                  minLength={8}
                  required
                />
              </div>
              <div>
                <label className="field-label" htmlFor="owner_password_confirmation">
                  {t("signup.passwordConfirm")} <span className="text-danger-text">*</span>
                </label>
                <input
                  id="owner_password_confirmation"
                  type="password"
                  className="field-input"
                  value={form.owner_password_confirmation}
                  onChange={(e) => set("owner_password_confirmation", e.target.value)}
                  autoComplete="new-password"
                  required
                />
              </div>
            </div>
            <p className="mt-1 text-caption text-ink-2">{t("signup.passwordHint")}</p>
            <FieldError errors={errors} name="owner_password" />

            {/* Honeypot: off-screen and out of the tab order; people never fill it. */}
            <div
              aria-hidden="true"
              style={{ position: "absolute", left: "-10000px", width: 1, height: 1, overflow: "hidden" }}
            >
              <label htmlFor="website">Website</label>
              <input
                id="website"
                name="website"
                tabIndex={-1}
                autoComplete="off"
                value={form.website}
                onChange={(e) => set("website", e.target.value)}
              />
            </div>

            <label className="mt-5 flex cursor-pointer items-start gap-2 text-dense text-ink">
              <input
                type="checkbox"
                className="mt-1 accent-[var(--pl-accent)]"
                checked={form.accept_terms}
                onChange={(e) => set("accept_terms", e.target.checked)}
              />
              <span>
                <Trans
                  i18nKey="signup.acceptTerms"
                  components={{
                    terms: <ContentLink slug="terms" />,
                    privacy: <ContentLink slug="privacy" />,
                  }}
                />
              </span>
            </label>
            <FieldError errors={errors} name="accept_terms" />

            <div className="mt-6 flex flex-col-reverse gap-3 sm:flex-row">
              <button
                type="button"
                className="btn-secondary sm:w-1/3"
                onClick={() => setStep(1)}
                disabled={submitting}
              >
                {t("signup.back")}
              </button>
              <button
                type="submit"
                className="btn-primary flex-1"
                disabled={submitting || !form.accept_terms}
              >
                {submitting ? t("signup.submitting") : t("signup.submit")}
              </button>
            </div>
          </form>
        )}
      </>
    );
  }

  return (
    <div className="min-h-screen" style={PUBLIC_BACKDROP}>
      <PublicHeader />
      <main className="mx-auto w-full max-w-xl px-4 pb-16 pt-4 sm:px-6 sm:pt-8">
        <h1 className="text-page-title font-bold text-ink">{t("signup.title")}</h1>
        <p className="mb-6 mt-1 text-body text-ink-2">{t("signup.subtitle")}</p>
        <div className="card p-5 sm:p-6">{body}</div>
        <p className="mt-6 text-center text-sm text-ink-2">
          {t("pricing.haveAccount")}{" "}
          <Link to="/login" className="font-semibold text-accent-text hover:underline">
            {t("pricing.signIn")}
          </Link>
        </p>
      </main>
    </div>
  );
}
